/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

// Browser-level wallpaper preference, matching darkMode.ts's storage/event
// pattern so App.tsx can react without a page reload and stay in sync across
// tabs via the native `storage` event.

const WALLPAPER_KEY = "nyrvex-wallpaper";

export type WallpaperType = "preset" | "url" | "upload";

export interface Wallpaper {
  type: WallpaperType;
  value: string; // preset id, external URL, or a data: URL for uploads
}

export const getWallpaper = (): Wallpaper | null => {
  const raw = localStorage.getItem(WALLPAPER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.value === "string" &&
      (parsed.type === "preset" || parsed.type === "url" || parsed.type === "upload")
    ) {
      return parsed as Wallpaper;
    }
  } catch {
    // ignore malformed value
  }
  return null;
};

export const setWallpaper = (wallpaper: Wallpaper | null): void => {
  if (wallpaper) {
    localStorage.setItem(WALLPAPER_KEY, JSON.stringify(wallpaper));
  } else {
    localStorage.removeItem(WALLPAPER_KEY);
  }
  // The native `storage` event only fires in *other* tabs -- dispatch our own
  // so the current tab's App.tsx picks up the change immediately too.
  window.dispatchEvent(new CustomEvent("wallpaperchange"));
};
